import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Home } from "lucide-react";
import { useT } from "@/lib/i18n";

interface Props {
  titleEn: string;
  titleBn: string;
  introEn?: string;
  introBn?: string;
  children: ReactNode;
}

export function ContentPage({ titleEn, titleBn, introEn, introBn, children }: Props) {
  const { t, lang } = useT();
  const title = lang === "bn" ? titleBn : titleEn;
  const intro = lang === "bn" ? introBn : introEn;

  return (
    <div className="mx-auto max-w-3xl px-3 py-6 sm:px-4 sm:py-8 md:px-6 md:py-12">
      <nav className="mb-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="inline-flex items-center gap-1 hover:text-accent">
          <Home size={12} /> {t("common.home")}
        </Link>
        <ChevronRight size={12} />
        <span className={lang === "bn" ? "font-bn" : ""}>{title}</span>
      </nav>

      <header className="mb-8">
        <h1 className={`text-3xl font-bold tracking-tight md:text-4xl ${lang === "bn" ? "font-bn" : ""}`}>
          {title}
        </h1>
        <p className={`mt-1 text-sm text-muted-foreground ${lang === "bn" ? "font-bn font-normal" : ""}`}>
          {lang === "bn" ? titleEn : titleBn}
        </p>
        {intro && (
          <p className={`mt-4 text-base text-foreground/80 ${lang === "bn" ? "font-bn" : ""}`}>{intro}</p>
        )}
      </header>

      <div className="space-y-10">{children}</div>
    </div>
  );
}

interface SectionProps {
  id?: string;
  headingEn: string;
  headingBn: string;
  children: ReactNode;
}

export function ContentSection({ id, headingEn, headingBn, children }: SectionProps) {
  const { lang } = useT();
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className={`mb-3 text-xl font-bold tracking-tight md:text-2xl ${lang === "bn" ? "font-bn" : ""}`}>
        {lang === "bn" ? headingBn : headingEn}
      </h2>
      <div className={`space-y-3 text-sm leading-relaxed text-foreground/85 md:text-base ${lang === "bn" ? "font-bn" : ""}`}>
        {children}
      </div>
    </section>
  );
}

interface BiTextProps {
  en: ReactNode;
  bn: ReactNode;
}

/** Renders only the active language's content. */
export function BiText({ en, bn }: BiTextProps) {
  const { lang } = useT();
  return <>{lang === "bn" ? bn : en}</>;
}
